import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, FileText, Clock, AlertCircle, ChevronRight, RotateCcw, CheckCircle, XCircle, MinusCircle,
  Trash2, Ban, UserPlus, Send, X, AlertTriangle, ExternalLink, Download, ChevronDown,
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import CompleteAssignmentModal from './CompleteAssignmentModal';
import './DesignTaskDetail.css';

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const STATES = [
  { n: 1,  name: 'Initiated',     short: 'Init'   },
  { n: 2,  name: 'Classified',    short: 'Class'  },
  { n: 3,  name: 'Assessed',      short: 'Impact' },
  { n: 4,  name: 'Drafting',      short: 'Draft'  },
  { n: 5,  name: 'Tech rev.',     short: 'Review' },
  { n: 6,  name: 'Indep. verif.', short: 'Verify' },
  { n: 7,  name: 'Config upd.',   short: 'Config' },
  { n: 8,  name: 'Ready rel.',    short: 'Ready'  },
  { n: 9,  name: 'Released',      short: 'Rel.'   },
  { n: 10, name: 'Closed',        short: 'Close'  },
  { n: 11, name: 'Cancelled',     short: 'Cancel' },
];

const SCOPE_LABEL = {
  'initial-creation':   'Initial creation',
  'change-to-released': 'Change',
  'hybrid':             'Hybrid',
};

const URGENCY_LABEL = {
  'routine':         'Routine',
  'expedited':       'Expedited',
  'safety-critical': 'Safety-critical',
};

const ROLE_LABEL = {
  'classifier':              'Classifier',
  'impact-signer-technical': 'Technical impact',
  'impact-signer-structural':'Structural impact',
  'impact-signer-compliance':'Compliance impact',
  'impact-signer-baseline':  'Baseline impact',
  'author':                  'Author',
  'technical-reviewer':      'Technical reviewer',
  'independent-verifier':    'Independent verifier',
  'configuration-updater':   'Configuration update',
  'releaser':                'Release authoriser',
  'closer':                  'Closer',
};

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function initials(name, email) {
  const source = (name || email || '?').trim();
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'today · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'yesterday · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' }) + ' · ' +
                       d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────

function DesignTaskDetail({ user }) {
  const { projectId, dtId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);   // { designTask, assignments, transitions }
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completeFor, setCompleteFor] = useState(null);  // assignment being completed

  // ─── HDO actions ────────────────────────────────────────────────
  const [people, setPeople] = useState([]);        // all enrolled persons (for picker)
  const [myRole, setMyRole] = useState(null);      // current user's DAS primaryRole
  const [showDelete, setShowDelete]   = useState(false);
  const [showCancel, setShowCancel]   = useState(false);
  const [showAuthor, setShowAuthor]   = useState(false);
  const [showRelease, setShowRelease] = useState(false);  // S9 → S10 confirm
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);  // generic retry-advance

  const fetchAll = useCallback(async () => {
    try {
      setError('');
      const [dtRes, pRes, peopleRes] = await Promise.all([
        axios.get(`/projects/${projectId}/designtasks/${dtId}`),
        axios.get(`/projects/${projectId}`),
        axios.get('/people').catch(() => ({ data: [] })),  // soft-fail if not enrolled
      ]);
      setData(dtRes.data);
      setProject(pRes.data);
      const list = peopleRes.data || [];
      setPeople(list);
      const me = list.find(p => String(p.user?._id) === String(user._id));
      setMyRole(me?.primaryRole || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Design Task.');
    } finally {
      setLoading(false);
    }
  }, [projectId, dtId, user._id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCompleted = () => {
    setCompleteFor(null);
    fetchAll();  // refetch so the new state + assignments show
  };

  // ─── HDO action handlers ──────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const res = await axios.delete(`/projects/${projectId}/designtasks/${dtId}`);
      // Navigate back to the project DT list after successful delete
      navigate(`/project/${projectId}`, { state: { flash: res.data?.message } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete Design Task.');
      setShowDelete(false);
    }
  };

  const handleCancel = async (reason) => {
    try {
      await axios.post(`/projects/${projectId}/designtasks/${dtId}/cancel`, {
        cancellationReason: reason,
      });
      setShowCancel(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel Design Task.');
    }
  };

  const handleSetAuthor = async (userId) => {
    try {
      await axios.put(`/projects/${projectId}/designtasks/${dtId}`, {
        assignedAuthor: userId,
      });
      setShowAuthor(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set author.');
    }
  };

  const handleAdvanceToReleased = async () => {
    try {
      // Phase 1 stand-in for the 3DEXPERIENCE release-confirmation webhook.
      await axios.post(`/projects/${projectId}/designtasks/${dtId}/transitions/10`, {
        overrideJustification: 'Phase 1 manual release confirmation (no 3DX integration yet).',
      });
      setShowRelease(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to advance to Released.');
    }
  };

  const handleAddEvidenceItem = async ({ identifier, externalId, currentRevision, maturityState }) => {
    try {
      const created = await axios.post(`/projects/${projectId}/evidenceitems`, {
        identifier,
        externalId,
        currentRevision,
        maturityState,
      });
      // Append the new item to the DT's affectedEvidenceItems array
      const existingIds = (data.designTask.affectedEvidenceItems || []).map(i => String(i._id || i));
      await axios.put(`/projects/${projectId}/designtasks/${dtId}`, {
        affectedEvidenceItems: [...existingIds, created.data._id],
      });
      setShowAddEvidence(false);
      fetchAll();
    } catch (err) {
      // Surface error inside the modal via re-throw
      throw err;
    }
  };

  const handleAdvanceForward = async (justification) => {
    try {
      const currentState = data?.designTask?.procedureState;
      if (!currentState || currentState >= 10) return;
      const targetState = currentState + 1;
      await axios.post(
        `/projects/${projectId}/designtasks/${dtId}/transitions/${targetState}`,
        { overrideJustification: justification }
      );
      setShowAdvance(false);
      fetchAll();
    } catch (err) {
      throw err;
    }
  };

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '60px 0' }}>
      <div className="loading-spinner"></div>
    </div>
  );

  if (error) return (
    <div className="dt-detail-error">
      <button className="dt-back" onClick={() => navigate(`/project/${projectId}`)}>
        <ArrowLeft size={16} />
      </button>
      <p>{error}</p>
    </div>
  );

  const { designTask: dt, assignments, transitions } = data;
  const yourActiveAssignment = assignments.find(a =>
    String(a.assignedUser?._id || a.assignedUser) === String(user._id) &&
    !a.isImmutable &&
    a.primaryStatus !== 'done'
  );
  const currentStateAssignments = assignments.filter(a => a.procedureState === dt.procedureState);

  return (
    <div className="dt-detail">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="dt-detail-header">
        <button className="dt-back" onClick={() => navigate(`/project/${projectId}`)} title="Back to project">
          <ArrowLeft size={16} />
        </button>
        <span className="dt-crumb">{project?.name}</span>
        <span className="dt-crumb-sep">/</span>
        <span className="dt-detail-ident">{dt.identifier}</span>
        <div className="dt-detail-header-spacer" />
        <AuditExportMenu projectId={projectId} dtId={dt._id} identifier={dt.identifier} />
      </div>

      {/* ── Title + pills ────────────────────────────────────── */}
      <h1 className="dt-detail-title">{dt.title}</h1>

      <div className="dt-detail-pills">
        <span className="dt-pill dt-pill-scope">{SCOPE_LABEL[dt.scopeType]}</span>
        <span className={`dt-pill dt-pill-urgency dt-urgency-${dt.urgency}`}>
          {URGENCY_LABEL[dt.urgency]}
        </span>
        <span className="dt-pill dt-pill-state">
          State {dt.procedureState} — {STATES.find(s => s.n === dt.procedureState)?.name
                                       || (dt.procedureState === 11 ? 'Cancelled' : 'Unknown')}
        </span>
        {dt.validatedClassification && (
          <span className="dt-pill dt-pill-class">{dt.validatedClassification.replace('-', ' ').toUpperCase()}</span>
        )}
      </div>

      {/* ── State stepper ────────────────────────────────────── */}
      <StateStepper currentState={dt.procedureState} cancelled={dt.procedureState === 11} />

      {/* ── HDO actions row + warnings ───────────────────────── */}
      {myRole === 'HDO' && dt.procedureState !== 11 && dt.procedureState !== 10 && (
        <HdoActionsBar
          dt={dt}
          onDelete={() => setShowDelete(true)}
          onCancel={() => setShowCancel(true)}
          onPickAuthor={() => setShowAuthor(true)}
          onMarkReleased={() => setShowRelease(true)}
          onAdvance={() => setShowAdvance(true)}
        />
      )}

      {/* Warning banner: author not yet set, but DT is approaching/in States 3-4 */}
      {myRole === 'HDO'
        && !dt.assignedAuthor
        && [3, 4].includes(dt.procedureState) && (
        <div className="dt-banner-warn">
          <AlertCircle size={16} />
          <div>
            <strong>Author not yet assigned.</strong> The DT can't advance to Drafting (State 4) without one.
            Pick the author now — anyone with Lead or Assist competence on the affected subjects is eligible.
          </div>
          <button className="dt-banner-action" onClick={() => setShowAuthor(true)}>
            Pick author →
          </button>
        </div>
      )}

      {/* Warning banner: DT at State 9 awaiting release confirmation */}
      {myRole === 'HDO' && dt.procedureState === 9 && (
        <div className="dt-banner-info">
          <CheckCircle size={16} />
          <div>
            <strong>Released — awaiting closure.</strong> Once 3DEXPERIENCE confirms the release callback,
            this DT will auto-advance to State 10. For Phase 1, you can advance it manually.
          </div>
          <button className="dt-banner-action" onClick={() => setShowRelease(true)}>
            Mark released → State 10
          </button>
        </div>
      )}

      {/* ── Grid: main + sidebar ─────────────────────────────── */}
      <div className="dt-detail-grid">
        <div>
          {/* Your assignment — prominent CTA card */}
          {yourActiveAssignment && (
            <YourAssignmentCard
              assignment={yourActiveAssignment}
              onComplete={() => setCompleteFor(yourActiveAssignment)}
              onStatusChange={fetchAll}
            />
          )}

          {/* Description */}
          {dt.description && (
            <div className="dt-card dt-card-soft">
              <p className="dt-card-title">Description</p>
              <div className="dt-desc">{dt.description}</div>
            </div>
          )}

          {/* Current-state assignments */}
          {currentStateAssignments.length > 0 && (
            <StateAssignmentsCard
              assignments={currentStateAssignments}
              stateName={STATES.find(s => s.n === dt.procedureState)?.name || ''}
              currentUserId={user._id}
              onComplete={(a) => setCompleteFor(a)}
            />
          )}

          {/* Activity timeline */}
          <ActivityTimeline transitions={transitions} />
        </div>

        <div>
          <PeoplePanel dt={dt} assignments={assignments} currentUserId={user._id} />
          <SubjectsPanel subjects={dt.affectedSubjects || []} />
          <EvidencePanel
            items={dt.affectedEvidenceItems || []}
            canEdit={myRole === 'HDO' || String(dt.assignedAuthor?._id || dt.assignedAuthor) === String(user._id)}
            onAddItem={() => setShowAddEvidence(true)}
          />
        </div>
      </div>

      {completeFor && (
        <CompleteAssignmentModal
          assignment={completeFor}
          projectId={projectId}
          dtId={dtId}
          onClose={() => setCompleteFor(null)}
          onCompleted={handleCompleted}
        />
      )}

      {showDelete && (
        <DeleteConfirmModal
          dt={dt}
          onClose={() => setShowDelete(false)}
          onConfirm={handleDelete}
        />
      )}

      {showCancel && (
        <CancelDTModal
          dt={dt}
          onClose={() => setShowCancel(false)}
          onConfirm={handleCancel}
        />
      )}

      {showAuthor && (
        <AuthorPickerModal
          dt={dt}
          people={people}
          onClose={() => setShowAuthor(false)}
          onConfirm={handleSetAuthor}
        />
      )}

      {showRelease && (
        <ConfirmReleasedModal
          dt={dt}
          onClose={() => setShowRelease(false)}
          onConfirm={handleAdvanceToReleased}
        />
      )}

      {showAddEvidence && (
        <AddEvidenceItemModal
          dt={dt}
          onClose={() => setShowAddEvidence(false)}
          onConfirm={handleAddEvidenceItem}
        />
      )}

      {showAdvance && (
        <AdvanceForwardModal
          dt={dt}
          onClose={() => setShowAdvance(false)}
          onConfirm={handleAdvanceForward}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════

function StateStepper({ currentState, cancelled }) {
  return (
    <div className={`dt-stepper ${cancelled ? 'dt-stepper-cancelled' : ''}`}>
      {STATES.map(s => {
        let cls = 'dt-step';
        if (s.n === 11) {
          // Off-path terminal — visually separated from 1-10.
          cls += ' dt-step-offpath';
          if (cancelled) cls += ' dt-step-offpath-active';
        } else if (cancelled) {
          cls += ' dt-step-muted';
        } else if (s.n < currentState) {
          cls += ' dt-step-done';
        } else if (s.n === currentState) {
          cls += ' dt-step-current';
        }
        return (
          <div key={s.n} className={cls}>
            <span className="dt-step-num">{s.n}</span>
            <span className="dt-step-short">{s.short}</span>
            <span className="dt-step-name">{s.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function YourAssignmentCard({ assignment, onComplete, onStatusChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleStatusClick = async (newStatus) => {
    if (newStatus === assignment.primaryStatus) return;  // no-op
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await axios.put(`/assignments/${assignment._id}/status`, { primaryStatus: newStatus });
      if (onStatusChange) await onStatusChange();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to update status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dt-card dt-your-assignment">
      <div className="dt-ya-header">
        <span className="dt-ya-tag">
          <span className="dt-ya-dot" /> Your assignment
        </span>
        {assignment.slaTargetAt && (
          <span className="dt-card-meta">
            <Clock size={11} style={{ marginRight: 4, verticalAlign: '-2px' }} />
            SLA {new Date(assignment.slaTargetAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
      <div className="dt-ya-role">
        {ROLE_LABEL[assignment.role] || assignment.role}
        {assignment.relatedSubject && (
          <span className="dt-ya-subject">
            {' · Subject '}{assignment.relatedSubject.number}
            {' — '}{assignment.relatedSubject.name}
          </span>
        )}
      </div>
      <div className="dt-ya-scope">
        Click <em>In progress</em> when you've started, then <em>Mark done with outcome</em> to advance the workflow.
      </div>

      <div className="dt-status-toggle">
        <button
          type="button"
          className={`dt-status-opt ${assignment.primaryStatus === 'to-do' ? 'active' : ''}`}
          onClick={() => handleStatusClick('to-do')}
          disabled={busy}
        >
          To do
        </button>
        <button
          type="button"
          className={`dt-status-opt ${assignment.primaryStatus === 'in-progress' ? 'active' : ''}`}
          onClick={() => handleStatusClick('in-progress')}
          disabled={busy}
        >
          In progress
        </button>
        <div
          className={`dt-status-opt dt-status-opt-readonly ${assignment.primaryStatus === 'done' ? 'active' : ''}`}
          title="Use 'Mark done with outcome' below"
        >
          Done
        </div>
      </div>

      {err && <div className="error-message" style={{ marginTop: 10, marginBottom: 0 }}>{err}</div>}

      <div className="dt-ya-actions">
        <button className="dt-btn-primary" onClick={onComplete}>
          Mark done with outcome <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function StateAssignmentsCard({ assignments, stateName, currentUserId, onComplete }) {
  return (
    <div className="dt-card dt-card-white">
      <div className="dt-card-header">
        <p className="dt-card-title">Active state · {stateName}</p>
        <span className="dt-card-meta">{assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}</span>
      </div>
      {assignments.map(a => {
        const isYou = String(a.assignedUser?._id || a.assignedUser) === String(currentUserId);
        const u = a.assignedUser;
        const userName = u?.name || u?.email || 'Unknown';
        const isOpen = !a.isImmutable && a.primaryStatus !== 'done';
        const hasDetails = a.attachmentUrl || a.outcomeReason || a.cveEngagementRef || a.reviewerJustification;
        return (
          <div key={a._id} className="dt-asn-block">
            <div className="dt-asn-row">
              <UserAvatar user={u} size="sm" isYou={isYou} />
              <div className="dt-asn-info">
                <div className="dt-asn-name">
                  {userName}{isYou && <span className="dt-asn-you-tag"> · you</span>}
                </div>
                <div className="dt-asn-role">
                  {ROLE_LABEL[a.role] || a.role}
                  {a.relatedSubject && (<span> · Subject {a.relatedSubject.number} ({a.relatedSubject.name})</span>)}
                </div>
              </div>
              <div className="dt-asn-status">
                <span className={`dt-ds-primary dt-ds-${a.primaryStatus}`}>
                  {a.primaryStatus === 'to-do' ? 'To do' :
                   a.primaryStatus === 'in-progress' ? 'In progress' : 'Done'}
                </span>
                <OutcomePill outcome={a.outcome} />
              </div>
              {isOpen && (
                <button
                  className={`dt-btn-ghost dt-btn-sm ${!isYou ? 'dt-btn-onbehalf' : ''}`}
                  onClick={() => onComplete(a)}
                  title={isYou ? 'Complete your assignment' : 'Complete on behalf (HDO override)'}
                >
                  {isYou ? 'Complete →' : 'Complete on behalf →'}
                </button>
              )}
            </div>
            {hasDetails && (
              <AssignmentDetails assignment={a} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Extra fields captured at completion — verification record URL,
 * outcome reason, CVE engagement reference, reviewer justification.
 * Displayed underneath the assignment row when present.
 */
function AssignmentDetails({ assignment }) {
  const isVerifier = assignment.role === 'independent-verifier';
  const attachmentLabel = isVerifier ? 'Verification record' : 'Attachment';

  return (
    <div className="dt-asn-details">
      {assignment.attachmentUrl && (
        <ExternalLinkRow
          label={attachmentLabel}
          url={assignment.attachmentUrl}
        />
      )}
      {assignment.cveEngagementRef && (
        <div className="dt-asn-detail-line">
          <span className="dt-asn-detail-label">CVE engagement</span>
          <span className="dt-asn-detail-value">{assignment.cveEngagementRef}</span>
        </div>
      )}
      {assignment.reviewerJustification && (
        <div className="dt-asn-detail-line">
          <span className="dt-asn-detail-label">Reviewer note</span>
          <span className="dt-asn-detail-value">{assignment.reviewerJustification}</span>
        </div>
      )}
      {assignment.outcomeReason && (
        <div className="dt-asn-detail-line">
          <span className="dt-asn-detail-label">Reason</span>
          <span className="dt-asn-detail-value">"{assignment.outcomeReason}"</span>
        </div>
      )}
    </div>
  );
}

/**
 * Renders a label + URL. URL is clickable when it's a real http(s)
 * link; otherwise displays as plain text (so non-URL attachments
 * like internal references still surface, just without the link).
 */
function ExternalLinkRow({ label, url }) {
  const isUrl = /^https?:\/\//i.test(String(url || '').trim());
  if (isUrl) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="dt-asn-detail-line dt-asn-detail-link"
        title={url}
      >
        <span className="dt-asn-detail-label">{label}</span>
        <span className="dt-asn-detail-value">{truncateUrl(url)}</span>
        <ExternalLink size={11} className="dt-asn-detail-extlink" />
      </a>
    );
  }
  return (
    <div className="dt-asn-detail-line">
      <span className="dt-asn-detail-label">{label}</span>
      <span className="dt-asn-detail-value">{url}</span>
    </div>
  );
}

function OutcomePill({ outcome }) {
  if (!outcome) return <span className="dt-ds-outcome pending">Outcome pending</span>;
  const map = {
    'accept':                { cls: 'accepted',  Icon: CheckCircle, label: 'Accepted' },
    'accept-with-comments':  { cls: 'accepted',  Icon: CheckCircle, label: 'Accepted (with comments)' },
    'authorise':             { cls: 'accepted',  Icon: CheckCircle, label: 'Authorised' },
    'reject':                { cls: 'rejected',  Icon: XCircle,     label: 'Rejected' },
    'hold':                  { cls: 'rejected',  Icon: AlertCircle, label: 'Held' },
    'escalate':              { cls: 'rejected',  Icon: AlertCircle, label: 'Escalated' },
    'skipped':               { cls: 'muted',     Icon: MinusCircle, label: 'Skipped' },
    'withdrawn':             { cls: 'muted',     Icon: MinusCircle, label: 'Withdrawn' },
  };
  const c = map[outcome] || { cls: 'pending', Icon: AlertCircle, label: outcome };
  const Icon = c.Icon;
  return (
    <span className={`dt-ds-outcome ${c.cls}`}>
      <Icon size={11} style={{ marginRight: 3, verticalAlign: '-2px' }} />
      {c.label}
    </span>
  );
}

function ActivityTimeline({ transitions }) {
  if (!transitions || transitions.length === 0) return null;
  const events = [...transitions].reverse(); // newest first
  return (
    <div className="dt-card dt-card-white">
      <div className="dt-card-header">
        <p className="dt-card-title">Audit log</p>
        <span className="dt-card-meta">{transitions.length} {transitions.length === 1 ? 'event' : 'events'}</span>
      </div>
      {events.map(t => (
        <div key={t._id} className="dt-act-item">
          <ActIcon transition={t} />
          <div className="dt-act-body">
            <span className="dt-act-emph">{t.actor?.name || t.actor?.email || '(system)'}</span>
            {' '}{t.action}
            {t.reason && <div className="dt-act-reason">{t.reason}</div>}
            <div className="dt-act-time">{relativeTime(t.createdAt)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActIcon({ transition }) {
  const isReturn = transition.fromState != null && transition.toState < transition.fromState;
  const isCancel = transition.toState === 11;
  const isAdvance = transition.fromState != null && transition.toState > transition.fromState;
  if (isCancel) {
    return <span className="dt-act-icon fail">×</span>;
  }
  if (isReturn) {
    return <span className="dt-act-icon warn"><RotateCcw size={11} /></span>;
  }
  if (isAdvance) {
    return <span className="dt-act-icon done">{transition.fromState}→{transition.toState}</span>;
  }
  return <span className="dt-act-icon">{transition.toState}</span>;
}

function PeoplePanel({ dt, assignments, currentUserId }) {
  const initiator = dt.initiator;
  const author = dt.assignedAuthor;
  const reviewers = uniqueByUserId(assignments.filter(a => a.role === 'technical-reviewer').map(a => a.assignedUser));
  const verifiers = uniqueByUserId(assignments.filter(a => a.role === 'independent-verifier').map(a => a.assignedUser));
  const releaser = assignments.find(a => a.role === 'releaser')?.assignedUser;

  return (
    <div className="dt-card dt-card-white">
      <p className="dt-card-title" style={{ marginBottom: 10 }}>People</p>
      <PersonRow label="Initiator" user={initiator} currentUserId={currentUserId} />
      <PersonRow label="Author"    user={author}    currentUserId={currentUserId} pending />
      <PersonGroupRow label="Reviewers" users={reviewers} currentUserId={currentUserId} pending />
      <PersonGroupRow label="Verifiers" users={verifiers} currentUserId={currentUserId} pending />
      <PersonRow label="Releaser" user={releaser} currentUserId={currentUserId} pending />
    </div>
  );
}

function uniqueByUserId(arr) {
  const seen = new Set();
  const out = [];
  for (const u of arr) {
    if (!u) continue;
    const id = String(u._id || u);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(u);
  }
  return out;
}

function PersonRow({ label, user, currentUserId, pending }) {
  if (!user) {
    return (
      <div className="dt-row">
        <span className="dt-row-label">{label}</span>
        <span className="dt-row-val muted">{pending ? 'Pending' : '—'}</span>
      </div>
    );
  }
  const isYou = String(user._id || user) === String(currentUserId);
  return (
    <div className="dt-row">
      <span className="dt-row-label">{label}</span>
      <span className="dt-row-val">
        <UserAvatar user={user} size="sm" isYou={isYou} />
        {user.name || user.email}
      </span>
    </div>
  );
}

function PersonGroupRow({ label, users, currentUserId, pending }) {
  if (!users || users.length === 0) {
    return (
      <div className="dt-row">
        <span className="dt-row-label">{label}</span>
        <span className="dt-row-val muted">{pending ? 'Pending' : '—'}</span>
      </div>
    );
  }
  return (
    <div className="dt-row">
      <span className="dt-row-label">{label}</span>
      <span className="dt-row-val">
        {users.map(u => {
          const isYou = String(u._id || u) === String(currentUserId);
          return (
            <UserAvatar key={u._id || u} user={u} size="sm" isYou={isYou} />
          );
        })}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Audit export — CSV or Markdown
// ─────────────────────────────────────────────────────────────────

function AuditExportMenu({ projectId, dtId, identifier }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportFormat = async (format) => {
    setBusy(true);
    setOpen(false);
    try {
      const res = await axios.get(
        `/projects/${projectId}/designtasks/${dtId}/audit-export?format=${format}`,
        { responseType: 'blob' }
      );
      const ext = format === 'md' ? 'md' : 'csv';
      const blob = new Blob([res.data], {
        type: format === 'md' ? 'text/markdown' : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${identifier}-audit-log.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Server may have sent a JSON error body even though we asked for a blob.
      // Read the blob as text to surface the real message.
      let msg = 'Audit export failed.';
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const parsed = JSON.parse(text);
            msg = parsed.error || text;
          } catch {
            msg = text || msg;
          }
        } else if (e.response?.data?.error) {
          msg = e.response.data.error;
        } else if (e.message) {
          msg = e.message;
        }
      } catch {/* ignore */}
      console.error('Audit export failed:', msg, e);
      alert('Audit export failed: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dt-audit-export">
      <button
        type="button"
        className="dt-btn-ghost dt-btn-sm"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        title="Export the audit trail"
      >
        <Download size={13} /> {busy ? 'Exporting…' : 'Export audit'} <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <div className="dt-audit-export-backdrop" onClick={() => setOpen(false)} />
          <div className="dt-audit-export-menu">
            <button
              type="button"
              className="dt-audit-export-item"
              onClick={() => exportFormat('md')}
            >
              <FileText size={13} /> Markdown (.md)
              <span className="dt-audit-export-hint">Human-readable audit document</span>
            </button>
            <button
              type="button"
              className="dt-audit-export-item"
              onClick={() => exportFormat('csv')}
            >
              <FileText size={13} /> CSV (.csv)
              <span className="dt-audit-export-hint">Spreadsheet-friendly transitions only</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SubjectsPanel({ subjects }) {
  if (!subjects.length) return null;
  return (
    <div className="dt-card dt-card-white">
      <p className="dt-card-title" style={{ marginBottom: 10 }}>Affected subjects</p>
      <div className="dt-subj-list">
        {subjects.map(s => (
          <span key={s._id} className={`dt-subj-pill dt-subj-${s.domain}`}>
            <span className="dt-subj-dot" /> #{s.number} {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function EvidencePanel({ items, canEdit, onAddItem }) {
  if (!items.length && !canEdit) return null;
  return (
    <div className="dt-card dt-card-white">
      <div className="dt-card-header">
        <p className="dt-card-title">Evidence items</p>
        <span className="dt-card-meta">
          {items.length ? `${items.length} linked` : 'None linked'}
        </span>
      </div>
      {items.map(i => <EvidenceItemRow key={i._id} item={i} />)}
      {canEdit && (
        <button className="dt-ev-add" onClick={onAddItem}>
          + Add evidence item
        </button>
      )}
    </div>
  );
}

function EvidenceItemRow({ item }) {
  const external = item.externalId || '';
  const isUrl = /^https?:\/\//i.test(external.trim());

  // Visual content is the same whether it's a link or a static row
  const body = (
    <>
      <FileText size={12} className="dt-ev-icon" />
      <div className="dt-ev-meta">
        <div className="dt-ev-id-row">
          <span className="dt-ev-id">{item.identifier}</span>
          {item.currentRevision && (
            <span className={`dt-ev-rev ${item.maturityState === 'in-work' ? 'new' : ''}`}>
              Rev {item.currentRevision} · {item.maturityState}
            </span>
          )}
        </div>
        {external && (
          <span className="dt-ev-ext" title={external}>
            {isUrl ? truncateUrl(external) : external}
          </span>
        )}
      </div>
      {isUrl && <ExternalLink size={11} className="dt-ev-extlink" />}
    </>
  );

  if (isUrl) {
    return (
      <a
        href={external}
        target="_blank"
        rel="noopener noreferrer"
        className="dt-ev-item dt-ev-item-link"
      >
        {body}
      </a>
    );
  }
  return <div className="dt-ev-item">{body}</div>;
}

// Trim long URLs to keep the panel readable (host + last path segment)
function truncateUrl(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop() || '';
    return last ? `${u.host}/…/${last}` : u.host;
  } catch {
    return url.length > 40 ? url.slice(0, 37) + '…' : url;
  }
}

// ─────────────────────────────────────────────────────────────────
// HDO Actions Bar — state-aware admin buttons
// ─────────────────────────────────────────────────────────────────

function HdoActionsBar({ dt, onDelete, onCancel, onPickAuthor, onMarkReleased, onAdvance }) {
  const state = dt.procedureState;
  const canDelete = [1, 2].includes(state);
  const canCancel = state >= 1 && state <= 10 && state !== 11;
  const canPickAuthor = [2, 3].includes(state) && !dt.assignedAuthor;
  const canMarkReleased = state === 9;
  // Generic forward-advance: covers stuck states where an auto-advance
  // failed due to missing prerequisites that have since been supplied
  // (e.g. evidence items added after author already submitted).
  const canAdvance = state >= 2 && state <= 9 && state !== 11;

  // Don't render the bar at all if there's nothing to show
  if (!canDelete && !canCancel && !canPickAuthor && !canMarkReleased && !canAdvance) return null;

  return (
    <div className="dt-hdo-actions">
      <span className="dt-hdo-eyebrow">HDO actions</span>
      <div className="dt-hdo-actions-row">
        {canPickAuthor && (
          <button className="dt-hdo-btn dt-hdo-btn-primary" onClick={onPickAuthor}>
            <UserPlus size={13} /> Pick author
          </button>
        )}
        {canMarkReleased && (
          <button className="dt-hdo-btn dt-hdo-btn-primary" onClick={onMarkReleased}>
            <Send size={13} /> Mark released → State 10
          </button>
        )}
        {canAdvance && !canMarkReleased && (
          <button className="dt-hdo-btn" onClick={onAdvance} title="Manually trigger the next state advance (HDO override)">
            <ChevronRight size={13} /> Advance forward
          </button>
        )}
        {canCancel && (
          <button className="dt-hdo-btn" onClick={onCancel}>
            <Ban size={13} /> Cancel DT
          </button>
        )}
        {canDelete && (
          <button className="dt-hdo-btn dt-hdo-btn-danger" onClick={onDelete}>
            <Trash2 size={13} /> Delete DT
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Delete confirmation modal
// ─────────────────────────────────────────────────────────────────

function DeleteConfirmModal({ dt, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const handleConfirm = async () => {
    setSubmitting(true);
    await onConfirm();
    // Parent navigates away; no need to setSubmitting(false)
  };
  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow" style={{ color: 'var(--accent)' }}>Permanent action</p>
            <h2>Delete Design Task?</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="dt-modal-body">
          <p className="dt-confirm-text">
            This will permanently delete <code className="dt-confirm-id">{dt.identifier}</code> along
            with all its assignments and state transition log entries.
            <strong> This cannot be undone.</strong>
          </p>
          <p className="dt-confirm-hint">
            Use delete only when the DT was raised in error. For DTs you raised correctly but no
            longer want to pursue, use <em>Cancel</em> instead — that moves the DT to State 11
            and preserves the audit trail.
          </p>
        </div>

        <div className="dt-modal-actions">
          <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
            Keep DT
          </button>
          <button type="button" className="dt-btn-danger" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Cancel DT modal
// ─────────────────────────────────────────────────────────────────

function CancelDTModal({ dt, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setErr('A cancellation reason is required.'); return; }
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Cancel Design Task</p>
            <h2>Move {dt.identifier} to State 11?</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <p className="dt-confirm-hint">
            Cancellation is terminal but non-destructive: the audit trail, completed assignments,
            and any uploaded evidence stay intact. The DT will be visible at State 11 in the
            project's DT list and the audit-export CSV.
          </p>

          <div className="dt-field">
            <label htmlFor="cancel-reason">Cancellation reason *</label>
            <textarea
              id="cancel-reason"
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. design path superseded by DT-0042; scope folded into wing-loads study; …"
              required
            />
            <span className="dt-field-hint">
              Appears in the State Transition Log entry and the audit-export CSV.
            </span>
          </div>

          {err && <div className="error-message">{err}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Keep open
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Cancelling…' : 'Cancel DT → State 11'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Author picker modal
// ─────────────────────────────────────────────────────────────────

const ROLE_LABELS = {
  'HDO':              'Head of Design Org',
  'ChiefEng':         'Chief Engineer',
  'Structures':       'Structures Lead',
  'ConfigCompliance': 'Configuration & Compliance',
  'IndepMonVerif':    'Independent Monitoring & Verification',
  'ExternalCVE':      'External CVE',
};

function AuthorPickerModal({ dt, people, onClose, onConfirm }) {
  const [userId, setUserId] = useState(
    dt.assignedAuthor ? String(dt.assignedAuthor._id || dt.assignedAuthor) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // Sort: HDO, ChiefEng, Structures, ConfigCompliance, IndepMonVerif, ExternalCVE
  const roleOrder = ['HDO', 'ChiefEng', 'Structures', 'ConfigCompliance', 'IndepMonVerif', 'ExternalCVE'];
  const sortedPeople = [...people].sort((a, b) => {
    const ai = roleOrder.indexOf(a.primaryRole);
    const bi = roleOrder.indexOf(b.primaryRole);
    if (ai !== bi) return ai - bi;
    return (a.user?.name || '').localeCompare(b.user?.name || '');
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) { setErr('Pick a person to assign as author.'); return; }
    setSubmitting(true);
    try {
      await onConfirm(userId);
    } catch {
      setSubmitting(false);
    }
  };

  const affectedSubjectNames = (dt.affectedSubjects || [])
    .map(s => s.name || `#${s.number || ''}`)
    .join(', ');

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Pick author</p>
            <h2>Who will draft {dt.identifier}?</h2>
            {affectedSubjectNames && (
              <p className="dt-confirm-hint" style={{ margin: '8px 0 0' }}>
                Affected subjects: <em>{affectedSubjectNames}</em>
              </p>
            )}
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <div className="dt-field">
            <label htmlFor="author-picker">Author *</label>
            <select
              id="author-picker"
              value={userId}
              onChange={e => setUserId(e.target.value)}
              required
            >
              <option value="">Select a person…</option>
              {sortedPeople.map(p => (
                <option key={p._id} value={p.user?._id || ''}>
                  {p.user?.name || p.user?.email} · {ROLE_LABELS[p.primaryRole] || p.primaryRole}
                </option>
              ))}
            </select>
            <span className="dt-field-hint">
              The author needs Lead or Assist competence on every affected subject. Cross-reference
              the Competence Matrix if you're not sure who's eligible.
            </span>
          </div>

          {err && <div className="error-message">{err}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Setting…' : (dt.assignedAuthor ? 'Update author' : 'Set author')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Confirm release → State 10 modal
// ─────────────────────────────────────────────────────────────────

function ConfirmReleasedModal({ dt, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } catch {
      setSubmitting(false);
    }
  };
  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Confirm release</p>
            <h2>Advance to State 10 (Closed)?</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="dt-modal-body">
          <p className="dt-confirm-text">
            This advances <code className="dt-confirm-id">{dt.identifier}</code> from State 9
            (Released) to State 10 (Closed).
          </p>
          <p className="dt-confirm-hint">
            In Phase 2 this transition will fire automatically when 3DEXPERIENCE posts the
            release-confirmation webhook. For Phase 1, the HDO confirms manually after the
            release callback has been verified out-of-band.
          </p>
        </div>

        <div className="dt-modal-actions">
          <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
            Not yet
          </button>
          <button type="button" className="dt-btn-primary" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Advancing…' : 'Advance to State 10'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Add Evidence Item modal
// ─────────────────────────────────────────────────────────────────

function AddEvidenceItemModal({ dt, onClose, onConfirm }) {
  const [identifier, setIdentifier] = useState('');
  const [externalId, setExternalId] = useState('');
  const [currentRevision, setCurrentRevision] = useState('A.1');
  const [maturityState, setMaturityState] = useState('in-work');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { setErr('Identifier is required.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await onConfirm({
        identifier: identifier.trim(),
        externalId: externalId.trim() || null,
        currentRevision: currentRevision.trim(),
        maturityState,
      });
    } catch (e2) {
      setErr(e2.response?.data?.error
          || e2.response?.data?.errors?.[0]?.msg
          || 'Failed to add evidence item.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Add evidence item</p>
            <h2>Register a deliverable for {dt.identifier}</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <p className="dt-confirm-hint">
            An Evidence Item is the procedural reference to something you produced — a CAD model,
            drawing, calc note, layup card. In Phase 2 these will sync from 3DEXPERIENCE; for
            Phase 1 you register them manually here.
          </p>

          <div className="dt-field">
            <label htmlFor="ev-id">Identifier *</label>
            <input
              id="ev-id"
              type="text"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="e.g. STR-SKIN-001"
              required
            />
            <span className="dt-field-hint">
              Short, project-unique ID. Becomes the canonical reference in the audit trail.
            </span>
          </div>

          <div className="dt-field">
            <label htmlFor="ev-ext">3DEXPERIENCE reference</label>
            <input
              id="ev-ext"
              type="text"
              value={externalId}
              onChange={e => setExternalId(e.target.value)}
              placeholder="e.g. 3DXP-doc-12345 or URL"
            />
          </div>

          <div className="dt-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label htmlFor="ev-rev">Current revision</label>
              <input
                id="ev-rev"
                type="text"
                value={currentRevision}
                onChange={e => setCurrentRevision(e.target.value)}
                placeholder="A.1"
              />
            </div>
            <div>
              <label htmlFor="ev-maturity">Maturity</label>
              <select id="ev-maturity" value={maturityState} onChange={e => setMaturityState(e.target.value)}>
                <option value="in-work">In Work</option>
                <option value="frozen">Frozen</option>
                <option value="released">Released</option>
                <option value="obsolete">Obsolete</option>
              </select>
            </div>
          </div>

          {err && <div className="error-message">{err}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add & link to DT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Advance forward modal — HDO retry of the auto-advance
// ─────────────────────────────────────────────────────────────────

const STATE_LABELS = {
  2: 'Classified', 3: 'Impact Assessment', 4: 'Drafting',
  5: 'Technical Review', 6: 'Independent Verification', 7: 'Configuration Update',
  8: 'Ready for Release', 9: 'Released', 10: 'Closed',
};

function AdvanceForwardModal({ dt, onClose, onConfirm }) {
  const targetState = dt.procedureState + 1;
  const targetLabel = STATE_LABELS[targetState] || `State ${targetState}`;
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) { setErr('A justification is required for HDO override.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await onConfirm(justification.trim());
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to advance.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">HDO override</p>
            <h2>Advance to State {targetState} — {targetLabel}?</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <p className="dt-confirm-hint">
            This triggers the state machine to attempt {dt.procedureState} → {targetState}.
            If the prerequisites are met (assignments done, data populated), it succeeds.
            If not, the backend will return a specific error explaining what's missing.
          </p>

          <div className="dt-field">
            <label htmlFor="adv-just">Override justification *</label>
            <textarea
              id="adv-just"
              rows={3}
              value={justification}
              onChange={e => setJustification(e.target.value)}
              placeholder="e.g. Evidence items registered after author submitted; manual retry of auto-advance."
              required
            />
            <span className="dt-field-hint">
              Logged to the State Transition Log and the audit-export CSV.
            </span>
          </div>

          {err && <div className="error-message">{err}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Advancing…' : `Advance to State ${targetState} →`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DesignTaskDetail;
